import { NavItem, RolePermission } from 'dashboard/models/role';
import { ApiListRelationship, ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

export enum UserStatus {
  Pending = 'pending',
  Active = 'active',
  Deactivated = 'deactivated',
}

export enum UserAvailability {
  Available = 'available',
  Busy = 'busy',
  Unavailable = 'unavailable',
}

export enum MfaMethod {
  Phone = 'phone',
  Totp = 'totp',
  Email = 'email',
  RecoveryCode = 'recovery-code',
}

export interface SlaFrequency {
  repeat: boolean;
  type: 'before' | 'after';
  time: number;
}

export interface UserNotificationPolicy {
  case: {
    assignment: ('dashboard' | 'email')[];
    sla: {
      'delivery-mechanism': ('dashboard' | 'email')[];
      frequency: SlaFrequency[];
    };
  };
}

export interface ExcessPermissions {
  'role-name': string;
  'excess-permissions-list': Permission[];
  'role-permissions-list': Permission[];
}

export interface UserAttributes {
  status?: UserStatus;
  availability?: UserAvailability;
  'email-address'?: string;
  'name-first'?: string;
  'name-last'?: string;
  'phone-number'?: string;
  timezone?: string;
  'invite-sent-at'?: string;
  'invite-expired'?: boolean;
  'enabled-mfa-methods'?: MfaMethod[];
  'available-mfa-methods'?: MfaMethod[];
  // Only for current users
  'totp-provisioning-uri'?: string;
  'mfa-email-confirmation-token-expires-at'?: string;
  'mfa-phone-confirmation-code-expires-at'?: string;
  'recovery-code'?: never; // This should never be serialized from User model
  permissions?: Permission[];
  'role-permissions'?: RolePermission[];
  'excess-permissions'?: ExcessPermissions[];
  'enabled-nav-items'?: NavItem[];
  'hidden-nav-items'?: NavItem[];
  'homepage-priority'?: string[];
  'default-landing-page'?: string;
  'case-assignment-policy'?: 'email' | 'none';
  'notification-policy'?: UserNotificationPolicy;
  'is-admin'?: boolean;
}

export interface UserRelationships {
  organization: ApiRelationship<ApiModel.Organization>;
  roles: ApiListRelationship<ApiModel.Role>;
  teams?: ApiListRelationship<ApiModel.Team>;
}

export interface UserRequestAttributes extends UserAttributes {
  'profile-picture-upload'?: File;
}

export interface UserResponseAttributes extends UserAttributes {
  'profile-picture-url'?: string;
}

export type User = ApiResource<
  UserRequestAttributes | UserResponseAttributes,
  UserRelationships,
  ApiModel.User | ApiModel.CurrentUser
>;
